import React, { useState, useEffect } from "react";
import axios from "axios";
import { useTheme } from "../context/ThemeContext";

function AdminDashboard() {
  const { darkMode } = useTheme();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get("/api/admin/stats");
      setStats(response.data);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load admin stats");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit"
    });
  };

  const formatHour = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit"
    });
  };

  if (loading) {
    return <div className="loading">Loading admin dashboard...</div>;
  }

  if (error) {
    return (
      <div style={{ padding: "20px" }}>
        <div style={{
          padding: "12px",
          background: "#fecaca",
          color: "#991b1b",
          borderRadius: "6px"
        }}>
          {error}
        </div>
      </div>
    );
  }

  const usage = stats?.usage || {};

  return (
    <div>
      <div className="content-header">
        <div>
          <h1 style={{ margin: "0 0 5px 0" }}>Admin Dashboard</h1>
          <p style={{ margin: 0, color: "#6b7280" }}>
            System overview and statistics
          </p>
        </div>
        <button className="btn btn-primary" onClick={fetchStats}>
          Refresh
        </button>
      </div>

      <div className="content-body">
        {/* System Stats */}
        <div className="card" style={{ marginBottom: "20px" }}>
          <div className="card-header" style={{ background: darkMode ? "#7f1d1d" : "#fee2e2", borderBottom: `2px solid ${darkMode ? "#dc2626" : "#b91c1c"}` }}>
            <h3 style={{ margin: 0, color: darkMode ? "#fecaca" : "#991b1b" }}>System Overview</h3>
          </div>
          <div className="card-body">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "15px" }}>
              <div style={{ textAlign: "center", padding: "15px", background: darkMode ? "#374151" : "#f3f4f6", borderRadius: "8px" }}>
                <div style={{ fontSize: "14px", color: darkMode ? "#9ca3af" : "#4b5563", marginBottom: "8px" }}>Total Users</div>
                <div style={{ fontSize: "32px", fontWeight: "700", color: darkMode ? "#fff" : "#111827" }}>
                  {stats?.totalUsers || 0}
                </div>
              </div>
              <div style={{ textAlign: "center", padding: "15px", background: darkMode ? "#374151" : "#f3f4f6", borderRadius: "8px" }}>
                <div style={{ fontSize: "14px", color: darkMode ? "#9ca3af" : "#4b5563", marginBottom: "8px" }}>Pro Users</div>
                <div style={{ fontSize: "32px", fontWeight: "700", color: "#2563eb" }}>
                  {stats?.usersByPlan?.pro || 0}
                </div>
              </div>
              <div style={{ textAlign: "center", padding: "15px", background: darkMode ? "#374151" : "#f3f4f6", borderRadius: "8px" }}>
                <div style={{ fontSize: "14px", color: darkMode ? "#9ca3af" : "#4b5563", marginBottom: "8px" }}>Enterprise Users</div>
                <div style={{ fontSize: "32px", fontWeight: "700", color: "#7c3aed" }}>
                  {stats?.usersByPlan?.enterprise || 0}
                </div>
              </div>
              <div style={{ textAlign: "center", padding: "15px", background: darkMode ? "#374151" : "#f3f4f6", borderRadius: "8px" }}>
                <div style={{ fontSize: "14px", color: darkMode ? "#9ca3af" : "#4b5563", marginBottom: "8px" }}>Admin Users</div>
                <div style={{ fontSize: "32px", fontWeight: "700", color: "#dc2626" }}>
                  {stats?.usersByRole?.admin || 0}
                </div>
              </div>
            </div>
            <div style={{ marginTop: "20px", paddingTop: "15px", borderTop: `1px solid ${darkMode ? "#4b5563" : "#e5e7eb"}` }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "15px" }}>
                <div style={{ textAlign: "center", padding: "12px", background: darkMode ? "#374151" : "#f3f4f6", borderRadius: "6px" }}>
                  <div style={{ fontSize: "12px", color: darkMode ? "#9ca3af" : "#4b5563", marginBottom: "4px" }}>Total SFMC Accounts</div>
                  <div style={{ fontSize: "20px", fontWeight: "600", color: darkMode ? "#fff" : "#111827" }}>
                    {stats?.totalAccounts || 0}
                  </div>
                </div>
                <div style={{ textAlign: "center", padding: "12px", background: darkMode ? "#374151" : "#f3f4f6", borderRadius: "6px" }}>
                  <div style={{ fontSize: "12px", color: darkMode ? "#9ca3af" : "#4b5563", marginBottom: "4px" }}>Total Monitoring Configs</div>
                  <div style={{ fontSize: "20px", fontWeight: "600", color: darkMode ? "#fff" : "#111827" }}>
                    {stats?.totalConfigs || 0}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Usage Monitoring Section */}
        <div className="card" style={{ marginBottom: "20px" }}>
          <div className="card-header" style={{ background: darkMode ? "#1e3a5f" : "#dbeafe", borderBottom: `2px solid ${darkMode ? "#3b82f6" : "#2563eb"}` }}>
            <h3 style={{ margin: 0, color: darkMode ? "#bfdbfe" : "#1e40af" }}>Usage Monitoring</h3>
          </div>
          <div className="card-body">
            {/* Check Stats Grid */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px", marginBottom: "20px" }}>
              {/* Checks Today */}
              <div style={{ padding: "15px", background: darkMode ? "#374151" : "#f3f4f6", borderRadius: "8px" }}>
                <div style={{ fontSize: "13px", color: darkMode ? "#9ca3af" : "#6b7280", marginBottom: "10px", fontWeight: "600" }}>Checks Today</div>
                <div style={{ fontSize: "28px", fontWeight: "700", color: darkMode ? "#fff" : "#111827", marginBottom: "8px" }}>
                  {usage.checksToday?.total || 0}
                </div>
                <div style={{ display: "flex", gap: "12px", fontSize: "12px" }}>
                  <span style={{ color: "#16a34a" }}>{usage.checksToday?.healthy || 0} healthy</span>
                  <span style={{ color: "#eab308" }}>{usage.checksToday?.warning || 0} warning</span>
                  <span style={{ color: "#dc2626" }}>{usage.checksToday?.error || 0} error</span>
                </div>
              </div>

              {/* Checks This Week */}
              <div style={{ padding: "15px", background: darkMode ? "#374151" : "#f3f4f6", borderRadius: "8px" }}>
                <div style={{ fontSize: "13px", color: darkMode ? "#9ca3af" : "#6b7280", marginBottom: "10px", fontWeight: "600" }}>Checks This Week</div>
                <div style={{ fontSize: "28px", fontWeight: "700", color: darkMode ? "#fff" : "#111827", marginBottom: "8px" }}>
                  {usage.checksWeek?.total || 0}
                </div>
                <div style={{ display: "flex", gap: "12px", fontSize: "12px" }}>
                  <span style={{ color: "#16a34a" }}>{usage.checksWeek?.healthy || 0} healthy</span>
                  <span style={{ color: "#eab308" }}>{usage.checksWeek?.warning || 0} warning</span>
                  <span style={{ color: "#dc2626" }}>{usage.checksWeek?.error || 0} error</span>
                </div>
              </div>

              {/* Alerts Today */}
              <div style={{ padding: "15px", background: darkMode ? "#374151" : "#f3f4f6", borderRadius: "8px" }}>
                <div style={{ fontSize: "13px", color: darkMode ? "#9ca3af" : "#6b7280", marginBottom: "10px", fontWeight: "600" }}>Alerts Sent Today</div>
                <div style={{ fontSize: "28px", fontWeight: "700", color: usage.alertsToday > 0 ? "#dc2626" : (darkMode ? "#fff" : "#111827") }}>
                  {usage.alertsToday || 0}
                </div>
                <div style={{ fontSize: "12px", color: darkMode ? "#9ca3af" : "#6b7280" }}>
                  {usage.alertsWeek || 0} this week
                </div>
              </div>

              {/* Config Status Distribution */}
              <div style={{ padding: "15px", background: darkMode ? "#374151" : "#f3f4f6", borderRadius: "8px" }}>
                <div style={{ fontSize: "13px", color: darkMode ? "#9ca3af" : "#6b7280", marginBottom: "10px", fontWeight: "600" }}>Config Status</div>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px", fontSize: "13px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: "#16a34a" }}>Healthy</span>
                    <span style={{ fontWeight: "600" }}>{usage.statusDistribution?.healthy || 0}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: "#eab308" }}>Warning</span>
                    <span style={{ fontWeight: "600" }}>{usage.statusDistribution?.warning || 0}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: "#dc2626" }}>Error</span>
                    <span style={{ fontWeight: "600" }}>{usage.statusDistribution?.error || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Checks Per Hour (Last 24h) */}
            {usage.checksPerHour && usage.checksPerHour.length > 0 && (
              <div style={{ marginBottom: "20px" }}>
                <div style={{ fontSize: "13px", color: darkMode ? "#9ca3af" : "#6b7280", marginBottom: "10px", fontWeight: "600" }}>
                  Checks Per Hour (Last 24h)
                </div>
                <div style={{ display: "flex", alignItems: "flex-end", gap: "2px", height: "60px", padding: "10px", background: darkMode ? "#374151" : "#f3f4f6", borderRadius: "8px" }}>
                  {usage.checksPerHour.map((item, idx) => {
                    const maxCount = Math.max(...usage.checksPerHour.map(h => h.count));
                    const height = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
                    return (
                      <div
                        key={idx}
                        title={`${formatHour(item.hour)}: ${item.count} checks`}
                        style={{
                          flex: 1,
                          height: `${Math.max(height, 5)}%`,
                          background: "#3b82f6",
                          borderRadius: "2px 2px 0 0",
                          minWidth: "8px",
                          cursor: "pointer"
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Top Active Users & Configs Side by Side */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "20px" }}>
              {/* Top Users by Check Count */}
              {usage.checksPerUser && usage.checksPerUser.length > 0 && (
                <div>
                  <div style={{ fontSize: "13px", color: darkMode ? "#9ca3af" : "#6b7280", marginBottom: "10px", fontWeight: "600" }}>
                    Top Users by Checks (7 days)
                  </div>
                  <div style={{ background: darkMode ? "#374151" : "#f3f4f6", borderRadius: "8px", overflow: "hidden" }}>
                    {usage.checksPerUser.slice(0, 5).map((user, idx) => (
                      <div key={idx} style={{
                        padding: "10px 12px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderBottom: idx < 4 ? `1px solid ${darkMode ? "#4b5563" : "#e5e7eb"}` : "none"
                      }}>
                        <div>
                          <div style={{ fontSize: "13px", fontWeight: "500", color: darkMode ? "#fff" : "#111827" }}>{user.name}</div>
                          <div style={{ fontSize: "11px", color: darkMode ? "#9ca3af" : "#6b7280" }}>{user.email}</div>
                        </div>
                        <div style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#3b82f6",
                          background: darkMode ? "#1e3a5f" : "#dbeafe",
                          padding: "4px 10px",
                          borderRadius: "4px"
                        }}>
                          {user.checkCount}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Active Configs */}
              {usage.topActiveConfigs && usage.topActiveConfigs.length > 0 && (
                <div>
                  <div style={{ fontSize: "13px", color: darkMode ? "#9ca3af" : "#6b7280", marginBottom: "10px", fontWeight: "600" }}>
                    Most Active Configs (7 days)
                  </div>
                  <div style={{ background: darkMode ? "#374151" : "#f3f4f6", borderRadius: "8px", overflow: "hidden" }}>
                    {usage.topActiveConfigs.slice(0, 5).map((config, idx) => (
                      <div key={idx} style={{
                        padding: "10px 12px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderBottom: idx < 4 ? `1px solid ${darkMode ? "#4b5563" : "#e5e7eb"}` : "none"
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: "13px", fontWeight: "500", color: darkMode ? "#fff" : "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {config.name}
                          </div>
                          <div style={{ fontSize: "11px", color: darkMode ? "#9ca3af" : "#6b7280" }}>
                            {config.objectType} - {config.userEmail}
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginLeft: "10px" }}>
                          <span style={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            background: config.status === "healthy" ? "#16a34a" : config.status === "warning" ? "#eab308" : "#dc2626"
                          }} />
                          <span style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#3b82f6",
                            background: darkMode ? "#1e3a5f" : "#dbeafe",
                            padding: "4px 10px",
                            borderRadius: "4px"
                          }}>
                            {config.checkCount}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Active Sessions */}
        <div className="card" style={{ marginBottom: "20px" }}>
          <div className="card-header" style={{ background: darkMode ? "#166534" : "#dcfce7", borderBottom: `2px solid ${darkMode ? "#16a34a" : "#10b981"}` }}>
            <h3 style={{ margin: 0, color: darkMode ? "#dcfce7" : "#166534" }}>
              Active Sessions ({stats?.activeSessions?.length || 0})
            </h3>
            <p style={{ margin: "5px 0 0 0", fontSize: "13px", color: darkMode ? "#bbf7d0" : "#15803d" }}>
              Users active in the last 15 minutes
            </p>
          </div>
          <div className="card-body">
            {stats?.activeSessions && stats.activeSessions.length > 0 ? (
              <table className="table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Company</th>
                    <th>Role</th>
                    <th>IP Address</th>
                    <th>Last Activity</th>
                    <th>Session Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.activeSessions.map((session, index) => {
                    const lastActivity = new Date(session.last_activity);
                    const sessionStarted = new Date(session.session_started);
                    const minutesAgo = Math.floor((new Date() - lastActivity) / 60000);
                    const sessionDuration = Math.floor((lastActivity - sessionStarted) / 60000);

                    return (
                      <tr key={index}>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "50%",
                              background: minutesAgo < 5 ? "#16a34a" : "#f59e0b",
                              display: "inline-block"
                            }} />
                            <strong>{session.first_name} {session.last_name}</strong>
                          </div>
                        </td>
                        <td>{session.email}</td>
                        <td>{session.company || "-"}</td>
                        <td>
                          <span style={{
                            padding: "2px 8px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            background: session.role === "admin" ? "#fee2e2" : "#e5e7eb",
                            color: session.role === "admin" ? "#991b1b" : "#374151"
                          }}>
                            {session.role}
                          </span>
                        </td>
                        <td style={{ fontFamily: "monospace", fontSize: "12px" }}>
                          {session.ip_address || "N/A"}
                        </td>
                        <td>
                          <span style={{ color: minutesAgo < 5 ? "#16a34a" : "#6b7280" }}>
                            {minutesAgo === 0 ? "Just now" : `${minutesAgo}m ago`}
                          </span>
                        </td>
                        <td style={{ color: "#6b7280" }}>
                          {sessionDuration < 60 ? `${sessionDuration}m` : `${Math.floor(sessionDuration / 60)}h ${sessionDuration % 60}m`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="empty-state">
                <p>No active sessions</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Users */}
        <div className="card">
          <div className="card-header">
            <h3>Recent Registrations</h3>
          </div>
          <div className="card-body">
            {stats?.recentUsers && stats.recentUsers.length > 0 ? (
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Company</th>
                    <th>Plan</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Registered</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recentUsers.map((user) => (
                    <tr key={user.id}>
                      <td>{user.first_name} {user.last_name}</td>
                      <td>{user.email}</td>
                      <td>{user.company || "-"}</td>
                      <td>
                        <span style={{
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          background: user.plan === "enterprise" ? "#ede9fe" : "#dbeafe",
                          color: user.plan === "enterprise" ? "#5b21b6" : "#1d4ed8"
                        }}>
                          {user.plan?.toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          background: user.role === "admin" ? "#fee2e2" : "#e5e7eb",
                          color: user.role === "admin" ? "#991b1b" : "#374151"
                        }}>
                          {user.role}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          color: user.is_active ? "#16a34a" : "#dc2626",
                          fontWeight: "500"
                        }}>
                          {user.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>{formatDate(user.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="empty-state">
                <p>No recent users</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
